import { getApps, initializeApp } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { disableNetwork, enableNetwork, getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { resolveFirebaseAuthDomain } from "./firebaseAuthDomain";
import { isOrganizerEventsSharePath } from "../../features/publicShare/organizerEventsShareRoute";

/**
 * ======================================================
 * FIREBASE CORE
 * ======================================================
 *
 * Infrastructure-only module.
 *
 * Responsibilities:
 * - initialize Firebase once
 * - expose shared services
 * - keep bucket configuration in one place
 */

/* ======================================================
   CLIENT CONFIG
====================================================== */

function resolveAuthDomain() {
  if (typeof window === "undefined") {
    return "karango-a60c1.firebaseapp.com";
  }

  return resolveFirebaseAuthDomain({
    hostname: window.location?.hostname,
    userAgent: window.navigator?.userAgent,
  });
}

// Tests locaux uniquement : avec l'emulateur d'authentification, un projet
// « demo-* » isole toute la pile (aucun acces aux services reels). Sans
// emulateur, l'identifiant de production est toujours utilise.
const EMULATOR_PROJECT_ID =
  process.env.REACT_APP_USE_AUTH_EMULATOR === "true" &&
  /^demo-[a-z0-9-]+$/.test(String(process.env.REACT_APP_FIREBASE_PROJECT_ID || ""))
    ? process.env.REACT_APP_FIREBASE_PROJECT_ID
    : null;

const firebaseConfig = {
  apiKey: "AIzaSyATe3iuYBuNew6cQmi0hOdYG8IHOXGxth0",
  authDomain: resolveAuthDomain(),
  projectId: EMULATOR_PROJECT_ID || "karango-a60c1",
  storageBucket: "karango-a60c1.firebasestorage.app",
  messagingSenderId: "436727139807",
  appId: "1:436727139807:web:9c4b528d0f54fe5329086a",
  measurementId: "G-PHLMHTTQNC",
};

export const firebaseProjectId = firebaseConfig.projectId;
export const isAuthEmulatorEnabled =
  process.env.REACT_APP_USE_AUTH_EMULATOR === "true";

/* ======================================================
   SAFE APP INITIALIZATION
====================================================== */

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

/* ======================================================
   CORE SERVICES
====================================================== */

export const auth = getAuth(app);
// Les rares e-mails Firebase conserves en repli (emulateur, reset du mot de
// passe) doivent eux aussi rester coherents avec l'interface francaise.
auth.languageCode = "fr";
export const db = getFirestore(app);

if (isAuthEmulatorEnabled) {
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
}

if (process.env.REACT_APP_USE_FIRESTORE_EMULATOR === "true") {
  connectFirestoreEmulator(db, "localhost", 8080);
}

/*
 * Keep the active bucket first, but preserve a fallback
 * for older paths/configurations during migration.
 */
export const storageBuckets = Array.from(
  new Set([
    firebaseConfig.storageBucket,
    `${firebaseConfig.projectId}.appspot.com`,
  ].filter(Boolean))
);

export const storage = getStorage(app, `gs://${storageBuckets[0]}`);
export const storageFallback =
  storageBuckets[1] ? getStorage(app, `gs://${storageBuckets[1]}`) : null;

/* ======================================================
   OPTIONAL ANALYTICS
====================================================== */

export let analytics = null;

if (typeof window !== "undefined") {
  const hostname = String(window.location?.hostname || "").toLowerCase();
  const isLocalHost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1";
  const analyticsEnabledEnv = String(
    process.env.REACT_APP_ENABLE_ANALYTICS ?? (isLocalHost ? "false" : "true")
  ).toLowerCase();
  // Le lien public "/sorties/:token" ne doit jamais initialiser Analytics :
  // la collecte automatique embarque l'URL complete (donc le jeton) dans les
  // evenements de page vue. Ce test doit rester AVANT tout appel a
  // getAnalytics() ci-dessous, pas seulement empecher des evenements custom.
  const isPublicShareRoute = isOrganizerEventsSharePath(window.location?.pathname);
  const shouldLoadAnalytics =
    !isPublicShareRoute &&
    (analyticsEnabledEnv === "true" || analyticsEnabledEnv === "1");

  const loadAnalytics = () => {
    if (!shouldLoadAnalytics) {
      return;
    }

    isSupported()
      .then((supported) => {
        if (supported) {
          analytics = getAnalytics(app);
        }
      })
      .catch(() => {
        analytics = null;
      });
  };

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => loadAnalytics(), { timeout: 1500 });
  } else {
    window.setTimeout(loadAnalytics, 1200);
  }
}

/* ======================================================
   FIRESTORE WATCH STREAM AUTO-RECOVERY
====================================================== */

if (typeof window !== "undefined") {
  let recovering = false;

  function isFirestoreInternalError(msg) {
    return (
      msg.includes("INTERNAL ASSERTION FAILED") ||
      msg.includes("Unexpected state") ||
      msg.includes("FIRESTORE")
    );
  }

  function recover() {
    if (recovering) return;
    recovering = true;
    disableNetwork(db)
      .then(() => enableNetwork(db))
      .catch(() => {})
      .finally(() => { recovering = false; });
  }

  window.addEventListener("error", (event) => {
    const msg = String(event?.error?.message || event?.message || "");
    if (!isFirestoreInternalError(msg)) return;
    recover();
    event.preventDefault();
  });

  window.addEventListener("unhandledrejection", (event) => {
    const msg = String(event?.reason?.message || event?.reason || "");
    if (!isFirestoreInternalError(msg)) return;
    recover();
    event.preventDefault();
  });
}

export default app;
